import { useState } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, fmtEuro, fmtDeadline, deadlineDaysLeft, durationMin, capitalizeName } from './lib/format.js';
import { taskDueDate, isTaskOverdue } from './lib/deadlines.js';
import { ProjectAvatar, BudgetLight, ProgressRing, Avatar } from './components.jsx';
import { MentionText } from './mention-input.jsx';
import { IconSearch, IconPlus, IconChevronRight, IconPlay, IconCheck, IconFlag, IconTrend, IconFolder } from './icons.jsx';
import { GoalEditorModal } from './goal-editor-modal.jsx';
import { GoalMetricCurve } from './goal-curve.jsx';
import {
  currentMonthKey, metricMonthTarget, metricActual, metricPct, metricStatus,
  worstStatus, STATUS_META, RAMP, fmtGoalValue, fmtMonth, fmtMonthLong, fmtCadence,
} from './lib/goals.js';

// Open projects (active/closing) come first, then paused, then completed,
// then cancelled/archived. Within the same status, newest first.
const STATUS_RANK = { active: 0, closing: 1, on_hold: 2, done: 3, cancelled: 4, archived: 5 };
export function compareProjectsByStatus(a, b) {
  const ra = STATUS_RANK[a.status] ?? 99;
  const rb = STATUS_RANK[b.status] ?? 99;
  if (ra !== rb) return ra - rb;
  return (b.created_at || '').localeCompare(a.created_at || '');
}

// Reference date for "days to deadline" calculations: today for active/paused
// projects, frozen at completed_at for finished/cancelled/archived ones — so
// the countdown stops ticking once a project is closed.
const FROZEN_STATUSES = new Set(['done', 'cancelled', 'archived']);
export function projectReferenceDate(p) {
  if (p && FROZEN_STATUSES.has(p.status) && p.completed_at) {
    return new Date(p.completed_at);
  }
  return new Date();
}

// --- Status chip -------------------------------------------------------------
export function StatusChip({ status, wasOverBudget }) {
  const map = {
    active:    { label: 'Aktiv',                                      cls: 'chip' },
    closing:   { label: 'Abschluss läuft',                            cls: 'chip chip-warn' },
    done:      wasOverBudget
      ? { label: 'Abgeschlossen · überzogen',                         cls: 'chip chip-warn' }
      : { label: 'Abgeschlossen',                                     cls: 'chip chip-ok' },
    on_hold:   { label: 'Pausiert',                                   cls: 'chip' },
    cancelled: { label: 'Abgebrochen',                                cls: 'chip chip-crit' },
    archived:  { label: 'Archiviert',                                 cls: 'chip' },
  };
  const m = map[status] || map.active;
  return <span className={m.cls} style={{ height: 22 }}>{m.label}</span>;
}

// --- Projects list page ------------------------------------------------------
function ProjectsPage({ onOpenNewProject, onNavigate }) {
  const { projects, projectUsage, customerById } = useData();
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const filtered = projects.filter(p => p.title.toLowerCase().includes(q.toLowerCase()));
  const active = filtered.filter(p => p.status === 'active' || p.status === 'closing');
  const inactive = filtered.filter(p => p.status !== 'active' && p.status !== 'closing');
  const list = (showInactive ? filtered : active).slice().sort(compareProjectsByStatus);

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Projekte</h1>
        <div className="chip">{active.length} aktiv</div>
        {inactive.length > 0 && (
          <div className="seg" style={{ marginLeft: 8 }}>
            <button className={!showInactive ? 'on' : ''} onClick={() => setShowInactive(false)}>Aktiv</button>
            <button className={showInactive ? 'on' : ''} onClick={() => setShowInactive(true)}>Alle</button>
          </div>
        )}
        <div className="topbar-spacer"/>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          width: 240, height: 34, padding: '0 12px',
          borderRadius: 9, border: '1px solid var(--stroke)',
          background: 'var(--bg-card)', color: 'var(--ink-2)',
        }}>
          <IconSearch size={14}/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Projekt suchen…"
                 style={{ background: 'transparent', border: 0, outline: 0, color: 'var(--ink)', fontSize: 13, width: '100%', fontFamily: 'inherit' }}/>
        </div>
        <button className="btn btn-primary" onClick={onOpenNewProject}>
          <IconPlus size={14}/> Neues Projekt
        </button>
      </div>
      <div className="content">
        {list.length === 0 ? (
          <div className="card card-pad-lg">
            <div className="tiny muted">{projects.length === 0 ? 'Noch keine Projekte. Klick „Neues Projekt" oben rechts.' : 'Keine Projekte in dieser Sicht.'}</div>
          </div>
        ) : (
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px 140px 140px 100px 40px', padding: '12px 20px', borderBottom: '1px solid var(--stroke)', fontSize: 11, color: 'var(--ink-4)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <div>Projekt</div><div>Kunde</div><div>Deadline</div><div>Budget</div><div>Status</div><div/>
            </div>
            {list.map(p => {
              const usage = projectUsage[p.id] || { minutes: 0 };
              const pct = p.budget_minutes ? Math.round((usage.minutes / p.budget_minutes) * 100) : 0;
              const days = deadlineDaysLeft(p.deadline, projectReferenceDate(p));
              const c = customerById(p.customer_id);
              const isInactive = p.status !== 'active' && p.status !== 'closing';
              return (
                <div key={p.id} onClick={() => onNavigate({ view: 'project', id: p.id })}
                     style={{ display: 'grid', gridTemplateColumns: '1fr 180px 140px 140px 100px 40px', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid var(--stroke)', cursor: 'pointer', transition: 'background 150ms', opacity: isInactive ? 0.7 : 1 }}
                     onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                    <ProjectAvatar project={p} size={32}/>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                      <div className="tiny muted" style={{ display: 'flex', gap: 6, marginTop: 2, alignItems: 'center' }}>
                        {p.status !== 'active' && <StatusChip status={p.status} wasOverBudget={p.was_over_budget}/>}
                        {p.tags?.length > 0 && p.tags.slice(0,2).map(t => <span key={t}>· {t}</span>)}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>{c?.name || '—'}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                    {p.deadline ? (
                      <>
                        {fmtDeadline(p.deadline)}
                        <div className={`tiny ${days < 14 ? '' : 'muted'}`} style={{ color: days < 14 && p.status === 'active' ? 'var(--crit)' : undefined }}>{days} Tage</div>
                      </>
                    ) : <span className="muted">—</span>}
                  </div>
                  <div>
                    {p.budget_minutes ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div className="bar" style={{ flex: 1 }}><span style={{ width: `${Math.min(100,pct)}%`, background: pct < 70 ? 'var(--ok)' : pct < 90 ? 'var(--warn)' : 'var(--crit)' }}/></div>
                          <span className="mono tiny" style={{ color: 'var(--ink-2)' }}>{pct}%</span>
                        </div>
                        <div className="tiny muted" style={{ marginTop: 2 }}>{fmtMin(usage.minutes)} / {fmtMin(p.budget_minutes)}{p.budget_is_estimate && <span style={{ color: 'var(--warn)', fontWeight: 600 }}> · geschätzt</span>}</div>
                      </>
                    ) : <span className="muted tiny">—</span>}
                  </div>
                  <div>{p.budget_minutes && (p.status === 'active' || p.status === 'closing') && <BudgetLight pct={pct}/>}</div>
                  <div><IconChevronRight size={14}/></div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// --- Deadline history (collapsible) -----------------------------------------
function DeadlineHistory({ project, changes, profileById }) {
  const [open, setOpen] = useState(false);
  const projectChanges = (changes || [])
    .filter(c => c.project_id === project.id)
    .sort((a, b) => (b.changed_at || '').localeCompare(a.changed_at || ''));

  return (
    <div style={{ marginTop: 8 }}>
      <div className="tiny muted" style={{ lineHeight: 1.5 }}>
        Ursprüngliche Deadline: <b style={{ color: 'var(--ink-2)' }}>{fmtDeadline(project.original_deadline)}</b>
        {' · '}
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
        >
          {projectChanges.length}× verschoben {open ? '▴' : '▾'}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-sunken)', borderRadius: 8, border: '1px solid var(--stroke)' }}>
          {projectChanges.length === 0 ? (
            <div className="tiny muted">Keine Verschiebungen aufgezeichnet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {projectChanges.map(c => {
                const author = c.changed_by ? profileById(c.changed_by) : null;
                return (
                  <div key={c.id} style={{ borderLeft: '2px solid var(--stroke-2)', paddingLeft: 10 }}>
                    <div className="tiny" style={{ color: 'var(--ink-2)', fontWeight: 600 }}>
                      {fmtDeadline(c.old_deadline)} → {fmtDeadline(c.new_deadline)}
                    </div>
                    <div className="tiny muted" style={{ marginTop: 2 }}>
                      {new Date(c.changed_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      {author ? ` · ${capitalizeName(author.full_name || author.email)}` : ''}
                    </div>
                    <div className="tiny" style={{ marginTop: 4, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                      {c.reason}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Drive folder button -----------------------------------------------------
// Drei Zustände: Ordner vorhanden → öffnen; wird angelegt → Spinner; sonst →
// "Ordner anlegen" (deckt 'idle' und Retry nach 'error' ab).
function DriveButton({ project, onSync }) {
  const [busy, setBusy] = useState(false);
  const status = project.drive_sync_status;

  if (project.drive_folder_url) {
    return (
      <a className="btn" href={project.drive_folder_url} target="_blank" rel="noopener noreferrer"
         title="Projektordner in Google Drive öffnen">
        <IconFolder size={13}/> Drive-Ordner
      </a>
    );
  }
  if (status === 'pending' || busy) {
    return <button className="btn" disabled style={{ opacity: 0.6 }}><IconFolder size={13}/> Ordner wird angelegt…</button>;
  }
  async function run() {
    setBusy(true);
    try { await onSync(); }
    catch (e) { window.alert(`Drive-Ordner fehlgeschlagen:\n${e.message || e}`); }
    finally { setBusy(false); }
  }
  return (
    <button className="btn" onClick={run}
            title={status === 'error' ? (project.drive_sync_error || 'Letzter Versuch fehlgeschlagen — erneut versuchen') : 'Drive-Ordnerstruktur anlegen'}>
      <IconFolder size={13}/> {status === 'error' ? 'Drive-Ordner erneut' : 'Drive-Ordner anlegen'}
    </button>
  );
}

// --- Task assignee picker ----------------------------------------------------
// Small avatar button that opens a popover to assign a task to a team member.
// `members` = uid list for the project. In read-only mode it just shows the
// assigned avatar (or nothing). Closes on outside click via a fixed backdrop.
function TaskAssignee({ assigneeId, members, profileById, onAssign, canEdit }) {
  const [open, setOpen] = useState(false);
  const assignee = assigneeId ? profileById(assigneeId) : null;
  const name = assignee ? (capitalizeName(assignee.full_name) || assignee.email) : null;

  if (!canEdit) {
    return assignee
      ? <div title={`Zugewiesen: ${name}`} style={{ flex: '0 0 auto' }}><Avatar user={assignee} size={22}/></div>
      : null;
  }

  return (
    <div style={{ position: 'relative', flex: '0 0 auto' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
              title={name ? `Zugewiesen: ${name}` : 'Aufgabe zuweisen'}
              style={{ padding: 0, border: 0, background: 'transparent', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
        {assignee
          ? <Avatar user={assignee} size={22}/>
          : <div style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px dashed var(--stroke-2)', display: 'grid', placeItems: 'center', color: 'var(--ink-4)', fontSize: 13, lineHeight: 1 }}>+</div>}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }}/>
          <div style={{ position: 'absolute', top: 28, right: 0, zIndex: 41, minWidth: 190,
                        background: 'var(--bg-elev)', border: '1px solid var(--stroke)', borderRadius: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 6 }}>
            <div className="eyebrow" style={{ padding: '4px 8px' }}>Zuweisen an</div>
            {members.length === 0 ? (
              <div className="tiny muted" style={{ padding: '6px 8px' }}>Diesem Projekt ist noch kein Team zugewiesen.</div>
            ) : members.map(uid => {
              const u = profileById(uid);
              if (!u) return null;
              const active = assigneeId === uid;
              return (
                <button key={uid} type="button"
                        onClick={() => { onAssign(active ? null : uid); setOpen(false); }}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px',
                                 background: active ? 'var(--accent-soft)' : 'transparent', border: 0, borderRadius: 8,
                                 cursor: 'pointer', color: active ? 'var(--accent)' : 'var(--ink)', fontFamily: 'inherit', fontSize: 13, textAlign: 'left' }}>
                  <Avatar user={u} size={22}/>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{capitalizeName(u.full_name) || u.email}</span>
                  {active && <IconCheck size={12}/>}
                </button>
              );
            })}
            {assigneeId && (
              <button type="button" onClick={() => { onAssign(null); setOpen(false); }}
                      style={{ display: 'flex', width: '100%', padding: '6px 8px', marginTop: 4,
                               background: 'transparent', border: 0, borderTop: '1px solid var(--stroke)',
                               cursor: 'pointer', color: 'var(--ink-3)', fontFamily: 'inherit', fontSize: 12.5, textAlign: 'left' }}>
                Zuweisung entfernen
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// --- Task due date -----------------------------------------------------------
// Kompaktes Fälligkeitsdatum in der Aufgabenzeile. Ohne eigenes Datum gilt die
// Projekt-Deadline — die zeigen wir blass als Platzhalter an, damit klar ist,
// woran sich die Aufgabe sonst misst. Überfällig = rot.
function TaskDueDate({ task, project, canEdit, onChange }) {
  const effective = taskDueDate(task, project);
  const overdue = isTaskOverdue(task, project);
  const inherited = !task.due_date && !!project?.deadline;

  const label = effective
    ? new Date(effective).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : null;
  const title = effective
    ? `Fällig: ${fmtDeadline(effective)}${inherited ? ' (Deadline des Projekts)' : ''}`
    : 'Fälligkeitsdatum setzen';

  if (!canEdit) {
    return label ? (
      <div className="tiny mono" title={title} style={{
        flex: '0 0 auto', whiteSpace: 'nowrap',
        color: overdue ? 'var(--crit)' : 'var(--ink-3)',
        opacity: inherited ? 0.6 : 1,
      }}>{label}</div>
    ) : null;
  }

  return (
    <input
      type="date"
      className="input"
      value={task.due_date || ''}
      title={title}
      onChange={e => onChange(e.target.value || null)}
      style={{
        flex: '0 0 auto', width: 138, padding: '4px 6px', fontSize: 12,
        color: overdue ? 'var(--crit)' : (task.due_date ? 'var(--ink)' : 'var(--ink-4)'),
        borderColor: overdue ? 'var(--crit)' : undefined,
      }}
    />
  );
}

// --- Project detail page -----------------------------------------------------
function ProjectDetail({ id, onBack, onStartTimer, onEdit, onCloseProject }) {
  const {
    projects, timeEntries, profileById, customerById,
    membersByProject, projectUsage, currentProfile, isAdmin,
    involvedByProject, completionsByProject,
    setProjectStatus,
    tasks, addTask, toggleTaskDone, deleteTask, updateTask,
    deadlineChanges, syncDriveFolders,
  } = useData();
  const [statusBusy, setStatusBusy] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskAssignee, setNewTaskAssignee] = useState(null);
  const [newTaskDue, setNewTaskDue] = useState(null);
  const [taskBusy, setTaskBusy] = useState(false);

  const p = projects.find(x => x.id === id);
  if (!p) {
    return (
      <div className="content">
        <div className="card card-pad-lg">
          <div className="tiny muted">Projekt nicht gefunden.</div>
          <button className="btn" style={{ marginTop: 12 }} onClick={onBack}>Zurück</button>
        </div>
      </div>
    );
  }
  const c = customerById(p.customer_id);
  const usage = projectUsage[p.id] || { minutes: 0, euro: 0 };
  const pct = p.budget_minutes ? Math.round((usage.minutes / p.budget_minutes) * 100) : 0;
  const days = deadlineDaysLeft(p.deadline, projectReferenceDate(p));
  const tone = pct < 70 ? 'ok' : pct < 90 ? 'warn' : 'crit';
  const toneColor = pct < 70 ? 'var(--ok)' : pct < 90 ? 'var(--warn)' : 'var(--crit)';
  const toneLabel = pct < 70 ? "Im grünen Bereich" : pct < 90 ? "Bald eng" : "Kritisch";
  const entries = timeEntries.filter(e => e.project_id === p.id);
  const members = membersByProject[p.id] || [];

  const involvedIds = involvedByProject[p.id] || [];
  const completions = completionsByProject[p.id] || [];
  const completionByUser = Object.fromEntries(completions.map(c => [c.user_id, c]));
  const myCompletion = completionByUser[currentProfile.id];
  const iAmInvolved = involvedIds.includes(currentProfile.id);

  const isActive  = p.status === 'active';
  const isClosing = p.status === 'closing';
  const isOnHold  = p.status === 'on_hold';
  const isDone    = p.status === 'done';
  const isCancel  = p.status === 'cancelled';
  const isFinal   = isDone || isCancel;
  const overBudget = !!p.budget_minutes && usage.minutes > p.budget_minutes;

  async function changeStatus(status, prompt) {
    if (prompt && !window.confirm(prompt)) return;
    setStatusBusy(true);
    try { await setProjectStatus(p.id, status); }
    catch (e) { window.alert(e.message || 'Fehler'); }
    finally { setStatusBusy(false); }
  }

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <button className="btn btn-ghost" onClick={onBack} style={{ padding: '4px 8px' }}>← Projekte</button>
        <div style={{ width: 1, height: 20, background: 'var(--stroke)' }}/>
        <ProjectAvatar project={p} size={34}/>
        <div>
          <h1 style={{ fontSize: 18 }}>{p.title}</h1>
          <div className="tiny muted" style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{c?.name || '—'}{c?.city ? ` · ${c.city}` : ''}</span>
            {p.status !== 'active' && <StatusChip status={p.status} wasOverBudget={p.was_over_budget}/>}
          </div>
        </div>
        <div className="topbar-spacer"/>

        {/* Action buttons by status */}
        {isActive && p.budget_minutes && (
          <div className={`chip chip-${tone}`}><span className="dot-status" style={{ background: toneColor }}/> {toneLabel}</div>
        )}
        {isAdmin && isActive && onEdit && (
          <button className="btn" onClick={() => onEdit(p)} disabled={statusBusy}>Bearbeiten</button>
        )}
        {isAdmin && isActive && (
          <button className="btn" onClick={() => changeStatus('on_hold', 'Projekt pausieren?')} disabled={statusBusy}>
            Pausieren
          </button>
        )}
        {isAdmin && isOnHold && (
          <button className="btn" onClick={() => changeStatus('active')} disabled={statusBusy}>
            Wieder aktivieren
          </button>
        )}
        {isAdmin && isFinal && (
          <button className="btn" onClick={() => changeStatus('active', 'Projekt reaktivieren?')} disabled={statusBusy}>
            Reaktivieren
          </button>
        )}
        {(isActive || isClosing) && onCloseProject && (
          <button className="btn btn-primary" onClick={() => onCloseProject(p)}>
            {isClosing ? 'Abschluss verwalten' : overBudget ? 'Abschluss starten' : 'Erfolgreich abschließen'}
          </button>
        )}
        <DriveButton project={p} onSync={() => syncDriveFolders(p.id)} />
        {(isActive || isClosing) && (
          <button className="btn" onClick={onStartTimer}><IconPlay size={13}/> Zeit starten</button>
        )}
      </div>

      <div className="content">
        {/* Status banners */}
        {isClosing && iAmInvolved && !myCompletion && (
          <div className="card card-pad" style={{ marginBottom: 20, background: 'var(--warn-soft)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 22 }}>📝</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--warn)' }}>
                Begründung erforderlich
              </div>
              <div className="tiny" style={{ color: 'var(--ink-2)', marginTop: 2 }}>
                Das Projekt wurde überzogen — bitte trag deine Begründung (mind. 150 Zeichen) ein, damit der Admin abschließen kann.
              </div>
            </div>
            <button className="btn btn-primary" onClick={() => onCloseProject?.(p)}>
              Begründung eintragen
            </button>
          </div>
        )}

        {isClosing && (!iAmInvolved || myCompletion) && (
          <div className="card card-pad" style={{ marginBottom: 20, background: 'var(--warn-soft)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 22 }}>⏳</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--warn)' }}>
                Abschluss läuft — {completions.length} von {involvedIds.length} Begründungen erfasst
              </div>
              <div className="tiny" style={{ color: 'var(--ink-2)', marginTop: 2 }}>
                {myCompletion ? 'Deine Begründung ist gespeichert. ' : ''}
                Sobald alle Beteiligten eingereicht haben, kann der Admin endgültig abschließen.
              </div>
            </div>
            <button className="btn" onClick={() => onCloseProject?.(p)}>Status ansehen</button>
          </div>
        )}

        {isFinal && (
          <div className="card card-pad" style={{
            marginBottom: 20,
            background: isCancel ? 'var(--crit-soft)' : (p.was_over_budget ? 'var(--warn-soft)' : 'var(--ok-soft)'),
            border: '1px solid',
            borderColor: isCancel ? 'rgba(239,68,68,0.3)' : (p.was_over_budget ? 'rgba(245,158,11,0.3)' : 'rgba(16,185,129,0.3)'),
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <span style={{ fontSize: 22 }}>{isCancel ? '✕' : (p.was_over_budget ? '⚠️' : '🎉')}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: isCancel ? 'var(--crit)' : (p.was_over_budget ? 'var(--warn)' : 'var(--ok)') }}>
                {isCancel ? 'Projekt abgebrochen' :
                 p.was_over_budget ? 'Mit Überziehung abgeschlossen' : 'Erfolgreich abgeschlossen'}
                {p.completed_at && <> · {new Date(p.completed_at).toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}</>}
              </div>
            </div>
          </div>
        )}

        {isOnHold && (
          <div className="card card-pad" style={{ marginBottom: 20, background: 'var(--bg-sunken)', display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 22 }}>⏸</span>
            <div className="tiny" style={{ color: 'var(--ink-2)' }}>
              Projekt pausiert. Reaktiviere es oben rechts, um wieder Zeit zu erfassen.
            </div>
          </div>
        )}

        <div className="grid grid-12" style={{ marginBottom: 20 }}>
          <div className="card col-8 card-pad-lg">
            <div className="eyebrow">
              Budget
              {p.budget_is_estimate && p.budget_minutes ? (
                <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, color: 'var(--warn)', background: 'var(--warn-soft)', verticalAlign: 'middle' }}>GESCHÄTZT</span>
              ) : null}
            </div>
            {p.budget_minutes ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 12 }}>
                <ProgressRing pct={Math.min(100, pct)} size={140} stroke={12} accent={toneColor}>
                  <div style={{ textAlign: 'center' }}>
                    <div className="serif" style={{ fontSize: 28, fontWeight: 500, color: toneColor }}>{pct}%</div>
                    <div className="tiny muted">verbraucht</div>
                  </div>
                </ProgressRing>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>Zeit-Budget</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                    <div className="serif" style={{ fontSize: 22, fontWeight: 500 }}>{fmtMin(usage.minutes)}</div>
                    <div className="muted">von {fmtMin(p.budget_minutes)}</div>
                  </div>
                  <div className="bar thick" style={{ marginTop: 10 }}>
                    <span style={{ width: `${Math.min(100, pct)}%`, background: toneColor }}/>
                  </div>
                  {p.budget_euro && (
                    <>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)', margin: '18px 0 6px' }}>Geld-Budget</div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <div className="serif" style={{ fontSize: 22, fontWeight: 500 }}>{fmtEuro(usage.euro)}</div>
                        <div className="muted">von {fmtEuro(p.budget_euro)}</div>
                      </div>
                      <div className="bar thick" style={{ marginTop: 10 }}>
                        <span style={{ width: `${Math.min(100, (usage.euro/p.budget_euro)*100)}%`, background: toneColor }}/>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="tiny muted" style={{ marginTop: 10 }}>Kein Budget definiert.</div>
            )}
          </div>

          <div className="card col-4 card-pad-lg">
            <div className="eyebrow">Projekt</div>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <Field label="Deadline" value={
                p.deadline ? (
                  <div>
                    <div>
                      <span>{fmtDeadline(p.deadline)}</span>
                      <span className={`chip ${days < 14 ? 'chip-crit' : days < 30 ? 'chip-warn' : 'chip-ok'}`} style={{ marginLeft: 6 }}>in {days} Tagen</span>
                    </div>
                    {p.original_deadline && p.original_deadline !== p.deadline && (
                      <DeadlineHistory project={p} changes={deadlineChanges} profileById={profileById}/>
                    )}
                  </div>
                ) : '—'
              }/>
              <Field label="Team" value={
                members.length === 0 ? <span className="muted tiny">kein Team zugewiesen</span> :
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {members.map((uid, i) => {
                    const u = profileById(uid);
                    if (!u) return null;
                    return <div key={uid} style={{ marginLeft: i ? -8 : 0, border: '2px solid var(--bg-card)', borderRadius: '50%' }}><Avatar user={u} size={24}/></div>;
                  })}
                </div>
              }/>
              <Field label="Tags" value={p.tags?.length > 0 ? <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{p.tags.map(t => <span key={t} className="chip">{t}</span>)}</div> : <span className="muted tiny">keine</span>}/>
              {p.description && <Field label="Beschreibung" value={<span style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-2)' }}><MentionText text={p.description}/></span>}/>}
            </div>
          </div>
        </div>

        {/* Goal (Nordstern) — inherited from the project's customer */}
        <GoalSection customer={c} inherited/>

        {/* Abschluss-Begründungen. Sie entstehen nur im Abschluss-Prozess, also
            zeigen wir sie, sobald welche da sind — auch nach einem Abschluss,
            den nicht ein Admin ausgelöst hat. */}
        {completions.length > 0 && (
          <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
            <div className="card-title">
              <span>Abschluss-Begründungen</span>
              <span className="sub">{completions.length} {completions.length === 1 ? 'Eintrag' : 'Einträge'}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {completions.map(comp => {
                const u = profileById(comp.user_id);
                return (
                  <div key={comp.id} style={{ padding: 12, borderRadius: 10, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      {u && <Avatar user={u} size={26}/>}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{capitalizeName(u?.full_name) || u?.email || '—'}</div>
                        <div className="tiny muted">{new Date(comp.created_at).toLocaleDateString('de-DE')}</div>
                      </div>
                      <span className="chip chip-ok" style={{ height: 22 }}><IconCheck size={11}/> erfasst</span>
                    </div>
                    <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
                      <MentionText text={comp.reason}/>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tasks */}
        {(() => {
          const projectTasks = tasks.filter(t => t.project_id === p.id).sort((a, b) => (a.position || 0) - (b.position || 0));
          const openCount = projectTasks.filter(t => !t.done).length;
          const doneCount = projectTasks.length - openCount;
          const canEdit = !isFinal;

          async function addNew() {
            if (!newTaskTitle.trim() || taskBusy) return;
            setTaskBusy(true);
            try {
              await addTask({
                project_id: p.id, title: newTaskTitle,
                assignee_id: newTaskAssignee, due_date: newTaskDue,
              });
              setNewTaskTitle('');
              setNewTaskAssignee(null);
              setNewTaskDue(null);
            } catch (e) { window.alert(e.message || 'Fehler'); }
            finally { setTaskBusy(false); }
          }

          return (
            <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
              <div className="card-title">
                <span>Aufgaben</span>
                <span className="sub">
                  {projectTasks.length === 0 ? 'noch keine' : `${doneCount} / ${projectTasks.length} erledigt`}
                </span>
              </div>

              {projectTasks.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                  {projectTasks.map(t => {
                    const closer = t.done_by ? profileById(t.done_by) : null;
                    return (
                      <div key={t.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '8px 10px', borderRadius: 8,
                        background: t.done ? 'transparent' : 'var(--bg-sunken)',
                        border: '1px solid var(--stroke)',
                      }}>
                        <button
                          type="button"
                          onClick={() => canEdit && toggleTaskDone(t.id, !t.done)}
                          disabled={!canEdit}
                          style={{
                            width: 18, height: 18, borderRadius: 5,
                            border: t.done ? '0' : '1.5px solid var(--stroke-2)',
                            background: t.done ? 'var(--ok)' : 'transparent',
                            display: 'grid', placeItems: 'center',
                            cursor: canEdit ? 'pointer' : 'not-allowed',
                            color: '#fff', flex: '0 0 18px', padding: 0,
                          }}>
                          {t.done && <IconCheck size={11} stroke={3}/>}
                        </button>
                        <div style={{
                          flex: 1, minWidth: 0,
                          fontSize: 13,
                          textDecoration: t.done ? 'line-through' : 'none',
                          color: t.done ? 'var(--ink-3)' : 'var(--ink)',
                        }}>{t.title}</div>
                        <TaskDueDate
                          task={t}
                          project={p}
                          canEdit={canEdit && !t.done}
                          onChange={due => updateTask(t.id, { due_date: due })}
                        />
                        <TaskAssignee
                          assigneeId={t.assignee_id}
                          members={members}
                          profileById={profileById}
                          canEdit={canEdit}
                          onAssign={uid => updateTask(t.id, { assignee_id: uid })}
                        />
                        {t.done && closer && (
                          <div className="tiny muted" style={{ whiteSpace: 'nowrap' }}>
                            {capitalizeName(closer.full_name) || closer.email}
                          </div>
                        )}
                        {canEdit && (
                          <button className="btn btn-ghost" style={{ padding: '2px 6px', color: 'var(--ink-3)' }}
                                  onClick={() => deleteTask(t.id)} title="Aufgabe löschen">×</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {projectTasks.length === 0 && (
                <div className="tiny muted" style={{ padding: '6px 0 14px' }}>
                  Noch keine Aufgaben angelegt. {canEdit ? 'Trag unten welche ein.' : ''}
                </div>
              )}

              {canEdit && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    className="input"
                    placeholder="Neue Aufgabe…"
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }}
                  />
                  <TaskDueDate
                    task={{ due_date: newTaskDue, done: false }}
                    project={p}
                    canEdit={true}
                    onChange={setNewTaskDue}
                  />
                  <TaskAssignee
                    assigneeId={newTaskAssignee}
                    members={members}
                    profileById={profileById}
                    canEdit={true}
                    onAssign={setNewTaskAssignee}
                  />
                  <button className="btn btn-primary" onClick={addNew} disabled={!newTaskTitle.trim() || taskBusy}
                          style={{ opacity: (newTaskTitle.trim() && !taskBusy) ? 1 : 0.45 }}>
                    Hinzufügen
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        <div className="card card-pad-lg">
          <div className="card-title">
            <span>Zeiteinträge</span>
            <span className="sub">{entries.length} {entries.length === 1 ? 'Eintrag' : 'Einträge'}</span>
          </div>
          {entries.length === 0 ? (
            <div className="tiny muted" style={{ padding: '12px 4px' }}>Noch keine Zeiten erfasst.</div>
          ) : entries.map(e => {
            const dur = durationMin(e.from_time, e.to_time);
            const user = profileById(e.user_id) || currentProfile;
            return (
              <div key={e.id} className="row" style={{ gridTemplateColumns: '100px 28px 1fr 120px 80px' }}>
                <div className="tiny muted mono">{e.entry_date}</div>
                {user && <Avatar user={user} size={24}/>}
                <div>
                  <div style={{ fontSize: 13 }}>{e.description || <span className="muted">—</span>}</div>
                  <div className="tiny muted">{capitalizeName(user?.full_name) || user?.email || '—'}</div>
                </div>
                <div className="tiny mono muted">{e.from_time.slice(0,5)} – {e.to_time.slice(0,5)}</div>
                <div className="mono" style={{ fontSize: 12.5 }}>{fmtMin(dur)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="eyebrow" style={{ marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13.5 }}>{value}</div>
    </div>
  );
}

// --- Goal (Nordstern) section — customer-level goal, inherited by projects ----
function GoalSection({ customer, inherited = false }) {
  const {
    isAdmin, profileById, goalByCustomer, goalMembersByCustomer,
    metricsByGoal, progressByMetric, checkinsByGoal,
    updateGoalProgress,
  } = useData();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null); // metric id being edited inline
  const [valueDraft, setValueDraft] = useState('');
  const [busy, setBusy] = useState(false);

  if (!customer) return null;
  const goal = goalByCustomer[customer.id];

  // No goal yet: prompt admin to create one (members just see nothing).
  if (!goal) {
    if (!isAdmin) return null;
    return (
      <div className="card card-pad-lg" style={{ marginBottom: 20, borderStyle: 'dashed' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ fontSize: 22 }}>🎯</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Noch kein Kundenziel</div>
            <div className="tiny muted" style={{ marginTop: 2 }}>
              Definiere für {customer.name} einen Nordstern mit einem oder mehreren Kriterien (z. B. „20.000 Follower bis 06/2027" + „1 Podcast/Monat" + „3 Posts/Woche"). Alle Projekte des Kunden erben das Ziel; das Team reflektiert monatlich.
            </div>
          </div>
          <button className="btn btn-primary" onClick={() => setEditorOpen(true)}>
            <IconFlag size={13}/> Ziel festlegen
          </button>
        </div>
        {editorOpen && <GoalEditorModal customer={customer} goal={null} onClose={() => setEditorOpen(false)}/>}
      </div>
    );
  }

  const mk = currentMonthKey();
  const metrics = metricsByGoal[goal.id] || [];
  const checkins = (checkinsByGoal[goal.id] || []).slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const members = goalMembersByCustomer[customer.id] || [];

  const overall = STATUS_META[worstStatus(metrics.map(m => metricStatus(m, goal, progressByMetric[m.id] || [], mk)))];
  const daysLeft = deadlineDaysLeft(goal.target_date);

  const checkedInThisMonth = new Set(checkins.filter(c => c.period_month === mk).map(c => c.user_id));
  const pendingMembers = members.filter(uid => !checkedInThisMonth.has(uid));

  async function saveValue(metricId) {
    const n = parseFloat(valueDraft);
    if (Number.isNaN(n)) { setEditingMetric(null); return; }
    setBusy(true);
    try {
      await updateGoalProgress(metricId, mk, n);
      setEditingMetric(null);
    } catch (e) { window.alert(e.message || 'Fehler'); }
    finally { setBusy(false); }
  }

  return (
    <div className="card card-pad-lg" style={{ marginBottom: 20 }}>
      <div className="card-title">
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><IconFlag size={15}/> Ziel</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {metrics.length > 0 && (
            <span className="chip" style={{ height: 22, color: overall.color, background: overall.soft, borderColor: 'transparent' }}>
              {overall.emoji} {overall.label}
            </span>
          )}
          {isAdmin && (
            <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={() => setEditorOpen(true)}>Bearbeiten</button>
          )}
        </span>
      </div>

      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{goal.title}</div>
      <div className="tiny muted" style={{ marginBottom: 16 }}>
        {inherited ? `Geerbt von ${customer.name} · ` : ''}Zieltermin {fmtDeadline(goal.target_date)}{daysLeft != null && daysLeft >= 0 ? ` · noch ${daysLeft} Tage` : daysLeft != null ? ' · überfällig' : ''}
      </div>

      {/* Criteria */}
      {metrics.length === 0 ? (
        <div className="tiny muted" style={{ marginBottom: 16 }}>Noch keine Kriterien definiert.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {metrics.map(m => {
            const rows = progressByMetric[m.id] || [];
            const monthTarget = metricMonthTarget(m, goal, mk);
            const actual = metricActual(m, rows, mk);
            const pct = metricPct(m, goal, rows, mk);
            const meta = STATUS_META[metricStatus(m, goal, rows, mk)];
            const isRecurring = m.type === 'recurring';
            const editing = editingMetric === m.id;
            // Headline denominator: recurring → this month's target; cumulative → the goal.
            const headlineTarget = isRecurring ? monthTarget : (Number(m.target_value) || 0);
            return (
              <div key={m.id} style={{ padding: 12, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)', borderRadius: 10 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 600 }}>{m.label}</span>
                    <span className="tiny muted" style={{ marginLeft: 8 }}>
                      {isRecurring ? fmtCadence(m) : `Ziel bis ${fmtMonth(goal.target_date.slice(0, 7) + '-01')} · ${RAMP[m.ramp || 'linear'].label}`}
                    </span>
                  </div>
                  <span className="chip" style={{ height: 20, fontSize: 11, color: meta.color, background: meta.soft, borderColor: 'transparent' }}>{meta.emoji} {meta.label}</span>
                </div>

                {editing ? (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input className="input" type="number" autoFocus value={valueDraft} style={{ background: 'var(--bg-card)', maxWidth: 160 }}
                           onChange={e => setValueDraft(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') saveValue(m.id); if (e.key === 'Escape') setEditingMetric(null); }}/>
                    <button className="btn btn-primary" onClick={() => saveValue(m.id)} disabled={busy}>Speichern</button>
                    <button className="btn btn-ghost" onClick={() => setEditingMetric(null)} disabled={busy}>Abbrechen</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div className="mono" style={{ fontSize: 13, color: 'var(--ink)' }}>
                        {fmtGoalValue(actual, m.unit)} <span className="muted">/ {fmtGoalValue(headlineTarget, m.unit)}{isRecurring ? ' (Monat)' : ' (Ziel)'}</span>
                      </div>
                      <div style={{ flex: 1 }}/>
                      <button className="btn btn-ghost" style={{ padding: '2px 10px' }}
                              onClick={() => { setValueDraft(String(actual)); setEditingMetric(m.id); }}>
                        Stand melden
                      </button>
                    </div>
                    <div className="bar thick"><span style={{ width: `${pct}%`, background: meta.color }}/></div>
                    {!isRecurring && (
                      <>
                        <div style={{ marginTop: 10 }}>
                          <GoalMetricCurve metric={m} goal={goal} progressRows={rows} color={meta.color}/>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                          <span className="tiny muted" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 14, height: 0, borderTop: '1.5px dashed var(--ink-4)' }}/> Soll
                            <span style={{ width: 10, height: 10, borderRadius: 999, background: meta.color, marginLeft: 8 }}/> Ist
                          </span>
                          <span className="tiny muted">Soll {fmtMonth(mk)}: {fmtGoalValue(monthTarget, m.unit)}</span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Monthly check-ins */}
      <div>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          Monats-Check-ins · {fmtMonthLong(mk)}
          {members.length > 0 && <span className="muted" style={{ fontWeight: 400 }}> · {checkedInThisMonth.size}/{members.length} erledigt</span>}
        </div>

        {pendingMembers.length > 0 && (
          <div className="tiny" style={{ marginBottom: 10, color: 'var(--warn)' }}>
            Ausstehend: {pendingMembers.map(uid => capitalizeName(profileById(uid)?.full_name) || profileById(uid)?.email || '—').join(', ')}
          </div>
        )}

        {checkins.length === 0 ? (
          <div className="tiny muted">Noch keine Check-ins.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {checkins.slice(0, 8).map(c => {
              const u = profileById(c.user_id);
              const m = STATUS_META[c.confidence] || STATUS_META.on_track;
              return (
                <div key={c.id} style={{ padding: 12, borderRadius: 10, background: 'var(--bg-sunken)', border: '1px solid var(--stroke)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    {u && <Avatar user={u} size={24}/>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{capitalizeName(u?.full_name) || u?.email || '—'}</div>
                      <div className="tiny muted">{fmtMonth(c.period_month)}</div>
                    </div>
                    <span className="chip" style={{ height: 22, color: m.color, background: m.soft, borderColor: 'transparent' }}>{m.emoji} {m.label}</span>
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}><MentionText text={c.reflection}/></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editorOpen && <GoalEditorModal customer={customer} goal={goal} onClose={() => setEditorOpen(false)}/>}
    </div>
  );
}

export { ProjectsPage, ProjectDetail, GoalSection, TaskAssignee, TaskDueDate };
